'use client';

import {
  faEnvelope,
  faGlobe,
  faPhone,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Company,
  Office,
  openUrl,
  useBreakpoints,
} from '@nettbureau/featured-core';
import {
  Button,
  Chip,
  Divider,
  HStack,
  IconButton,
  Item,
  Link,
  Stack,
  Text,
  VStack,
} from '@nettbureau/vibrant-ui';
import { useTranslate } from '@tolgee/react';
import { sampleSize } from 'lodash-es';
import { Fragment, useContext, useState } from 'react';
import { FeaturedContainer } from '../container/FeaturedContainer';
import { ConfigContext } from '../provider/config-provider';

interface BasicCompaniesSectionProps {
  companies: (Company | Office)[];
  nonClientCompanies: (Company | Office)[];
  deadCompanies: (Company | Office)[];
}

export function BasicCompaniesSection({
  companies,
  deadCompanies,
  nonClientCompanies,
}: BasicCompaniesSectionProps) {
  const { showBasicProfileActionButtons } = useContext(ConfigContext);

  const { t } = useTranslate();

  const shouldShowBasicProfile = (override: boolean) => {
    return showBasicProfileActionButtons || override;
  };

  const [showAllEntities, setShowAllEntities] = useState(false);

  const filteredNonClientCompanies = showAllEntities
    ? nonClientCompanies
    : sampleSize(nonClientCompanies, 10);

  const filteredDeadCompanies = showAllEntities
    ? deadCompanies
    : sampleSize(deadCompanies, 10);

  const shouldShowMoreButton = () =>
    filteredDeadCompanies.length !== deadCompanies.length ||
    filteredNonClientCompanies.length !== nonClientCompanies.length;

  if ([...companies, ...deadCompanies, ...nonClientCompanies].length === 0) {
    return null;
  }

  return (
    <FeaturedContainer gutter={false}>
      <VStack padding={{ y: 'md' }} gap="md">
        <Fragment>
          {companies.map((attributes, key) => {
            const {
              configuration,
              address,
              postarea,
              postcode,
              internalLink,
              phone,
              email,
              website,
            } = attributes;

            const override =
              configuration.showBasicProfileActionsButtons ?? false;

            return (
              <BasicProfileRow
                {...attributes}
                showInactiveBadge={false}
                address={address}
                postarea={postarea}
                postcode={postcode}
                href={internalLink}
                phone={phone}
                email={email}
                website={website}
                showActionButtons={shouldShowBasicProfile(override)}
                key={key}
              />
            );
          })}

          {filteredNonClientCompanies.map((attributes, key) => {
            const {
              address,
              postarea,
              postcode,
              internalLink,
              phone,
              email,
              website,
            } = attributes;

            return (
              <BasicProfileRow
                {...attributes}
                showInactiveBadge={false}
                address={address}
                postarea={postarea}
                postcode={postcode}
                href={internalLink}
                phone={phone}
                email={email}
                website={website}
                showActionButtons={false}
                key={key}
              />
            );
          })}

          {filteredDeadCompanies.map((attributes, key) => {
            const {
              address,
              postarea,
              postcode,
              internalLink,
              phone,
              email,
              website,
            } = attributes;

            return (
              <BasicProfileRow
                {...attributes}
                showInactiveBadge={true}
                address={address}
                postarea={postarea}
                postcode={postcode}
                href={internalLink}
                phone={phone}
                email={email}
                website={website}
                showActionButtons={false}
                key={key}
              />
            );
          })}
        </Fragment>

        {shouldShowMoreButton() && (
          <HStack justify="center">
            <Button variant="bold" onPress={() => setShowAllEntities(true)}>
              {t('show_more')}
            </Button>
          </HStack>
        )}
      </VStack>
    </FeaturedContainer>
  );
}

interface BasicProfileRowProps {
  name: string;
  showInactiveBadge: boolean;
  href: string | null;
  address: string | null;
  postcode: string | null;
  postarea: string | null;
  phone: string | null;
  website: string | null;
  email: string | null;
  showActionButtons: boolean;
}

function BasicProfileRow(props: BasicProfileRowProps) {
  const { isMobile } = useBreakpoints();
  const {
    name,
    showInactiveBadge,
    href,
    address,
    postcode,
    postarea,
    email,
    website,
    phone,
    showActionButtons,
  } = props;

  const { t } = useTranslate();

  return (
    <VStack gap="md">
      <HStack gap="lg" align="center">
        <Stack
          gap={isMobile ? 'xs' : 'lg'}
          direction={isMobile ? 'vertical' : 'horizontal'}
        >
          {href ? (
            <Item>
              <Link
                data-featured-basic-companies-name
                href={href}
                weight="medium"
              >
                {name}
              </Link>
            </Item>
          ) : (
            <Item>
              <Text data-featured-basic-companies-name weight="medium">
                {name}
              </Text>
            </Item>
          )}

          {showInactiveBadge ? (
            <Chip color="gray">{t('graveyard')}</Chip>
          ) : (
            <Text
              size={isMobile ? 'xs' : 'md'}
              weight={isMobile ? 'light' : 'normal'}
              data-featured-basic-companies-address
            >
              {address}, {postcode} {postarea}
            </Text>
          )}
        </Stack>

        {showActionButtons && !showInactiveBadge && (
          <Fragment>
            <Item grow></Item>

            <HStack align="center" gap="md">
              {email && (
                <IconButton
                  size={isMobile ? 'sm' : 'md'}
                  aria-label="Email"
                  icon={faEnvelope}
                  onPress={() => openUrl(`mailto:${email}`, false)}
                />
              )}

              {phone && (
                <IconButton
                  size={isMobile ? 'sm' : 'md'}
                  aria-label="Phone"
                  icon={faPhone}
                  onPress={() => openUrl(`tel:${phone}`, false)}
                />
              )}

              {website && (
                <IconButton
                  size={isMobile ? 'sm' : 'md'}
                  aria-label="Website"
                  icon={faGlobe}
                  onPress={() => openUrl(website, true)}
                />
              )}
            </HStack>
          </Fragment>
        )}
      </HStack>

      <Divider />
    </VStack>
  );
}
